import { Logout as LogoutIcon } from "@mui/icons-material";
import { AppBar, Avatar, Box, Chip, createTheme, CssBaseline, IconButton, ThemeProvider, Toolbar, Typography } from "@mui/material";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes, useNavigate } from "react-router-dom";
import { AgentChatInterface } from "./components/AgentChatInterface";
import { AgentsPage } from "./components/AgentsPage";
import { ApprovalGuard } from "./components/ApprovalGuard";
import { AuthCallback } from "./components/AuthCallback";
import { AuthForm } from "./components/AuthForm";
import { CalendarPage } from "./components/CalendarPage";
import { CoachesPage } from "./components/CoachesPage";
import { DebugEventsPage } from "./components/DebugEventsPage";
import { DebugMenu } from "./components/DebugMenu";
import { DebugThreadPage } from "./components/DebugThreadPage";
import { MessageItemViewer } from "./components/MessageItemViewer";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { GoogleServicesPage } from "./components/GoogleServicesPage";
import { MaintenancePage } from "./components/MaintenancePage";
import { PluginInfoPage } from "./components/PluginInfoPage";
import { PrioritiesPage } from "./components/PrioritiesPage";
import { ProfilePage } from "./components/ProfilePage";
import { DebugAgentsRoute, DebugEventsRoute, DebugPluginsRoute, DebugThreadsRoute } from "./components/ProtectedRoute";
import { Sidebar } from "./components/Sidebar";
import { TodoListPage } from "./components/TodoListPage";
import { OnboardingFlow } from "./components/onboarding";
import { TutorialProvider } from "./contexts/TutorialContext";
import { useAnalytics } from "./hooks/useAnalytics";
import { useOnboarding } from "./hooks/useOnboarding";
import { OverviewPage } from "./pages/OverviewPage";
import { googleServicesService } from "./services/GoogleServicesService";
import { maintenanceService } from "./services/MaintenanceService";
import { pluginService } from "./services/PluginService";
import { auth } from "./services/firebase";
import { googleAuthService } from "./services/googleAuth";
import { createLogger } from "./utils/logger";

const logger = createLogger("App");

import "./App.css";

// AppBar content component that can use navigation
const AppBarContent: React.FC<{ user: User; onLogout: () => void }> = ({ user, onLogout }) => {
  const navigate = useNavigate();

  const handleProfileClick = () => {
    navigate("/profile");
  };

  return (
    <Toolbar>
      <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
        Costaff.ai
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <DebugMenu />
        <Chip
          avatar={<Avatar>{user.displayName?.[0] || user.email?.[0]}</Avatar>}
          label={user.displayName || user.email}
          variant="outlined"
          sx={{
            color: "white",
            borderColor: "white",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
          onClick={handleProfileClick}
        />
        <IconButton color="inherit" onClick={onLogout}>
          <LogoutIcon />
        </IconButton>
      </Box>
    </Toolbar>
  );
};

// Create a clean, neutral theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#2c3e50",
      light: "#34495e",
      dark: "#1a252f",
    },
    secondary: {
      main: "#95a5a6",
      light: "#bdc3c7",
      dark: "#7f8c8d",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2c3e50",
      secondary: "#7f8c8d",
    },
    divider: "#ecf0f1",
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          borderRadius: 8,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
  },
});

// Component that handles analytics inside Router context
function AppWithAnalytics() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  // Get onboarding state
  const onboardingState = useOnboarding();
  const { isOnboardingNeeded, isOnboardingComplete, isLoading: onboardingLoading } = onboardingState;

  // Initialize analytics (now inside Router context)
  const analytics = useAnalytics(user);

  useEffect(() => {
    // Check maintenance mode first
    const checkMaintenanceMode = async () => {
      try {
        const maintenanceStatus = await maintenanceService.isMaintenanceModeEnabled();
        setIsMaintenanceMode(!!maintenanceStatus);
      } catch (error) {
        logger.error("Failed to check maintenance mode:", error);
      }
    };

    checkMaintenanceMode();

    // Process OAuth callback if present
    const processOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const error = urlParams.get("error");

      if (code && !error) {
        logger.info("Processing OAuth callback");

        // Wait for Firebase Auth to be available
        const waitForAuth = () => {
          return new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(auth, (user) => {
              unsubscribe();
              resolve(user);
            });
          });
        };

        try {
          const firebaseUser = await waitForAuth();

          if (firebaseUser) {
            const user = await googleAuthService.handleCallback();
            if (user) {
              logger.info("OAuth callback processed successfully");
              // Clear URL parameters after processing
              window.history.replaceState({}, document.title, window.location.pathname);

              // Add a small delay to allow tokens to be stored before health checks
              await new Promise((resolve) => setTimeout(resolve, 2000));

              // Force refresh Google services to pick up the new tokens
              try {
                if (googleServicesService) {
                  await googleServicesService.forceCheck();
                }
              } catch (error) {
                logger.warn("Failed to refresh Google services after OAuth:", error);
              }
            } else {
              logger.warn("OAuth callback processed but no user returned");
            }
          } else {
            logger.warn("No Firebase user available for OAuth callback");
          }
        } catch (error) {
          logger.error("Error processing OAuth callback:", error);
        }
      } else if (error) {
        logger.error("OAuth error detected:", error);
      }
    };

    processOAuthCallback();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check email verification synchronously before proceeding to main app
        if (!user.emailVerified) {
          logger.warn("User signed in with unverified email, sending verification email");

          try {
            // Send verification email while user is still signed in
            const { sendVerificationEmail } = await import("./services/firebase");
            await sendVerificationEmail(user);
            logger.info("Verification email sent successfully");
          } catch (error) {
            logger.error("Failed to send verification email:", error);
          }

          // Don't sign out - let ApprovalGuard handle showing the email verification screen
          // This allows the user to stay signed in and see the verification screen
          // DO NOT call checkGoogleServicesStatus() for unverified users
          logger.debug("Skipping Google services check for unverified user");
        } else {
          // Only initialize polling for verified users
          logger.info("User is verified, initializing plugin service");
          pluginService.init();
        }

        // Add a small delay to ensure the user's authentication token is fully established
        await new Promise((resolve) => setTimeout(resolve, 200));
        setUser(user);
        setIsLoading(false);
      } else {
        setUser(user);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      // Track logout attempt
      analytics.logUserInteraction("button_click", "App", "logout");
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
      analytics.logError("AUTH_003", "Logout failed", {
        component: "App.tsx",
        userAction: "handle_logout",
        severity: "medium",
      });
    }
  };

  const handleAuthSuccess = () => {
    // Auth success is handled by the onAuthStateChanged listener
  };

  if (isLoading || onboardingLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  // Show maintenance page if maintenance mode is enabled
  if (isMaintenanceMode) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MaintenancePage />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary componentName="App">
        <TutorialProvider>
          <div className="App">
            {user && (
              <ApprovalGuard user={user}>
                {/* Show onboarding flow if needed */}
                {isOnboardingNeeded && !isOnboardingComplete ? (
                  <OnboardingFlow onboardingState={onboardingState} />
                ) : (
                  <>
                    <AppBar position="fixed" elevation={1} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                      <AppBarContent user={user} onLogout={handleLogout} />
                    </AppBar>

                    <Sidebar open={true} />

                    <Box
                      sx={{
                        display: "flex",
                        ml: "240px", // Match sidebar width
                        mt: "64px", // Match AppBar height
                        height: "calc(100vh - 64px)",
                        overflow: "auto",
                      }}
                    >
                      <Box sx={{ flex: 1, width: "100%" }}>
                        <Routes>
                          <Route path="/auth/google/callback" element={<AuthCallback />} />
                          <Route path="/login" element={<Navigate to="/" replace />} />
                          <Route path="/" element={<OverviewPage />} />
                          <Route path="/chat" element={<AgentChatInterface />} />
                          <Route path="/chat/:agentId" element={<AgentChatInterface />} />
                          <Route path="/google-services" element={<GoogleServicesPage />} />
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route
                            path="/debug/events"
                            element={
                              <DebugEventsRoute uid={user?.uid}>
                                <DebugEventsPage />
                              </DebugEventsRoute>
                            }
                          />
                          <Route
                            path="/debug/threads"
                            element={
                              <DebugThreadsRoute uid={user?.uid}>
                                <DebugThreadPage />
                              </DebugThreadsRoute>
                            }
                          />
                          <Route
                            path="/debug/agents"
                            element={
                              <DebugAgentsRoute uid={user?.uid}>
                                <AgentsPage />
                              </DebugAgentsRoute>
                            }
                          />
                          <Route
                            path="/debug/plugins"
                            element={
                              <DebugPluginsRoute uid={user?.uid}>
                                <PluginInfoPage />
                              </DebugPluginsRoute>
                            }
                          />
                          <Route path="/debug/messages" element={<MessageItemViewer />} />
                          <Route path="/plugin-info" element={<PluginInfoPage />} />
                          <Route path="/agents" element={<AgentsPage />} />
                          <Route path="/coaches" element={<CoachesPage />} />
                          <Route path="/calendar" element={<CalendarPage />} />
                          <Route path="/todo" element={<TodoListPage />} />
                          <Route path="/priorities" element={<PrioritiesPage />} />
                        </Routes>
                      </Box>
                    </Box>
                  </>
                )}
              </ApprovalGuard>
            )}

            {!user && (
              <Routes>
                <Route path="/auth/google/callback" element={<AuthCallback />} />
                <Route path="/login" element={<AuthForm onAuthSuccess={handleAuthSuccess} />} />
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
            )}
          </div>
        </TutorialProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

// Main App component that provides Router context
function App() {
  return (
    <Router>
      <AppWithAnalytics />
    </Router>
  );
}

export default App;
